import { useState, useCallback, useEffect } from 'react';

import apiFetch from '@scale/frontend-lib/api-fetch';

import {
  IProjectGroup,
  CreateProjectGroupRequest,
  UpdateProjectGroupRequest,
  CreateProjectGroupResponse,
  UpdateProjectGroupResponse,
  ProjectGroupDiff,
} from '@scale/shared/dailyPlan/types/api';
import { ProjectGroupLifecyle } from '@scale/shared/dailyPlan/dailyPlanProjectGroup';

const DAILY_PLANNING_BASE_URL = 'corp-api/daily-planning';
const PROJECT_GROUP_BASE_URL = 'corp-api/project-groups';

type DataWithStatus<T> = {
  data: T | undefined;
  loading: boolean;
  error?: Error;
};

function useProjectGroups(
  options: {
    data?: IProjectGroup[];
    error?: Error;
    loading?: boolean;
    skipRefetch?: boolean;
    status?: ProjectGroupLifecyle[];
    projectId?: string;
  } = {},
) {
  const [state, setState] = useState<DataWithStatus<IProjectGroup[]>>({
    data: undefined,
    error: undefined,
    loading: false,
    ...options,
  });

  const fetch = useCallback(async () => {
    setState({ data: undefined, error: undefined, loading: true });

    try {
      const data = await apiFetch(null, `${PROJECT_GROUP_BASE_URL}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        query: {
          status: options.status?.join('-'),
          projectId: options.projectId,
        },
      });

      setState({
        data,
        error: undefined,
        loading: false,
      });
    } catch (error) {
      setState({ data: undefined, error, loading: false });
    }
  }, []);

  useEffect(() => {
    fetch();
  }, [fetch]);

  const saveProjectGroup = useCallback(
    async ({
      name,
      projectIds,
      description,
      businessUnit,
      slaCadence,
      status,
      businessUnitProduct,
    }: CreateProjectGroupRequest) => {
      setState(prevState => ({ ...prevState, error: undefined, loading: true }));

      try {
        const res = await apiFetch<CreateProjectGroupResponse | UpdateProjectGroupResponse>(
          null,
          `${PROJECT_GROUP_BASE_URL}`,
          {
            method: 'POST',
            body: {
              name,
              projectIds,
              description,
              businessUnitProduct,
              slaCadence,
              status,
              businessUnit,
            },
          },
        );

        options.skipRefetch &&
          setState(prevState => ({ ...prevState, error: undefined, loading: false }));
        // To update the local data with the server data
        !options.skipRefetch && fetch();
        return res;
      } catch (error) {
        setState({ data: undefined, error, loading: false });
        throw new Error(error);
      }
    },
    [fetch, options.skipRefetch],
  );

  const editProjectGroup = useCallback(
    async (
      {
        projectGroupId,
        name,
        projectIds,
        description,
        businessUnitProduct,
        businessUnit,
        fulfillmentMechanism,
        slaCadence,
        status,
      }: UpdateProjectGroupRequest & { projectGroupId: string },
      diff: ProjectGroupDiff,
    ) => {
      setState(prevState => ({ ...prevState, error: undefined, loading: true }));

      try {
        await apiFetch(null, `${PROJECT_GROUP_BASE_URL}/${projectGroupId}`, {
          method: 'PUT',
          body: {
            name,
            projectIds,
            description,
            businessUnit,
            slaCadence,
            status,
            businessUnitProduct,
            fulfillmentMechanism,
            diff,
          },
        });

        // To update the local data with the server data
        fetch();
      } catch (error) {
        setState({ data: undefined, error, loading: false });
        throw new Error(error);
      }
    },
    [fetch],
  );

  return { ...state, saveProjectGroup, editProjectGroup, refetch: fetch };
}

type QueryOverviewType = {
  projectGroupId: string;
};

function useOverviewMetrics(
  query: QueryOverviewType,
  options: { data?: IProjectGroup; error?: Error; loading?: boolean } = {},
) {
  const [state, setState] = useState<DataWithStatus<IProjectGroup>>({
    data: undefined,
    error: undefined,
    loading: false,
    ...options,
  });

  const { projectGroupId } = query;

  const fetch = useCallback(async () => {
    setState({ data: undefined, error: undefined, loading: true });

    try {
      const data = await apiFetch(null, `${PROJECT_GROUP_BASE_URL}/${projectGroupId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      setState({
        data,
        error: undefined,
        loading: false,
      });
    } catch (error) {
      setState({ data: undefined, error, loading: false });
    }
  }, [projectGroupId]);

  useEffect(() => {
    fetch();
  }, [fetch]);

  const saveSla = useCallback(
    async (weeklyThroughputSLA: any) => {
      setState(prevState => ({ ...prevState, error: undefined, loading: true }));

      try {
        await apiFetch(null, `${DAILY_PLANNING_BASE_URL}/actions/update-weekly-throughput-sla`, {
          method: 'POST',
          body: {
            projectGroupId,
            weeklyThroughputSLA,
          },
        });

        // To update the local data with the server data
        fetch();
      } catch (error) {
        setState({ data: undefined, error, loading: false });
      }
    },
    [projectGroupId, fetch],
  );

  return { ...state, saveSla };
}

export { useProjectGroups, useOverviewMetrics };
